import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Collapse,
  Box,
  Chip,
  Avatar,
  Grid,
  Fade,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PersonIcon from "@material-ui/icons/Person";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";

import { formatearFechaEspanol, esHoy } from "../utils/dateUtilities";
import PortafolioCardContent from "./PortafolioCardContent";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { formatearFecha } from "src/utils/formatTime";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    transition: "all 0.2s ease",
    backgroundColor: "white",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    },
  },
  cardToday: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    "&:hover": {
      boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
    },
  },
  cardHeader: {
    padding: theme.spacing(2),
  },
  fechaContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  fechaChip: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: 500,
  },
  fechaChipToday: {
    backgroundColor: theme.palette.secondary.main,
    color: "white",
    fontWeight: 600,
  },
  descripcionPreview: {
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: 1.5,
    minHeight: "3em",
    marginBottom: theme.spacing(1),
  },
  metadataContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  empleadoInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  empleadoAvatar: {
    width: 24,
    height: 24,
    fontSize: "0.7rem",
    backgroundColor: theme.palette.grey[400],
  },
  cardActions: {
    padding: theme.spacing(1, 2),
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  expandButton: {
    transition: "transform 0.2s ease",
  },
  expandButtonRotated: {
    transform: "rotate(180deg)",
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}10`,
    },
  },
  deleteButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: `${theme.palette.error.main}10`,
    },
  },
  deleteButtonDisabled: {
    color: theme.palette.text.disabled,
  },
  todayBadge: {
    backgroundColor: theme.palette.secondary.main,
    color: "white",
    fontSize: "0.7rem",
    height: 20,
  },
  fileCount: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    height: 24,
  },
}));

const ROLES_ADMIN = ["ADMIN", "SUPER_ADMIN"];

export default function PortafolioCardList({
  portafolios,
  cliente,
  onEditarPortafolio,
  onDeleteFile,
  deletingFile,
  onDeletePortafolio,
  deletingPortafolio,
  rol,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [expandedCard, setExpandedCard] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const puedeEliminar = ROLES_ADMIN.includes(rol);

  const handleExpandClick = (portafolioId) => {
    setExpandedCard(expandedCard === portafolioId ? null : portafolioId);
  };

  const handleEliminarPortafolio = async (portafolio) => {
    const confirmacion = await AlertaModalV2({
      title: "¿Eliminar bitácora?",
      text: `Se eliminará la bitácora del ${formatearFecha(portafolio.fecha, "DD/MM/YYYY")}. Esta acción puede revertirse por un administrador.`,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      icon: "warning",
    }).fire();

    if (!confirmacion.isConfirmed) return;

    setDeletingId(portafolio.id);
    const resultado = await onDeletePortafolio(portafolio);
    setDeletingId(null);

    if (resultado?.success) {
      enqueueSnackbar("Bitácora eliminada correctamente", {
        variant: "success",
      });
    } else {
      enqueueSnackbar(resultado?.error || "Error al eliminar la bitácora", {
        variant: "error",
      });
    }
  };

  if (!portafolios || portafolios.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {portafolios.map((portafolio, index) => {
        const {
          creador,
          ultimaModificacion,
          mostrarUltimaEdicion,
          puedeModificar,
          archivosPortafolio,
        } = portafolio;
        const esFechaHoy = esHoy(portafolio.fecha);
        const isExpanded = expandedCard === portafolio.id;
        const archivoCount = archivosPortafolio?.length || 0;

        return (
          <Grid item xs={12} key={`portafolio_${portafolio.id || index}`}>
            <Fade in={true} timeout={200 + index * 50}>
              <Card
                className={
                  esFechaHoy
                    ? `${classes.card} ${classes.cardToday}`
                    : classes.card
                }
              >
                <CardContent className={classes.cardHeader}>
                  {/* Fecha y badges */}
                  <div className={classes.fechaContainer}>
                    <CalendarTodayIcon color="action" fontSize="small" />
                    <Chip
                      label={formatearFechaEspanol(portafolio.fecha)}
                      className={
                        esFechaHoy ? classes.fechaChipToday : classes.fechaChip
                      }
                      size="small"
                    />
                    {esFechaHoy && (
                      <Chip
                        label="HOY"
                        className={classes.todayBadge}
                        size="small"
                      />
                    )}
                    {archivoCount > 0 && (
                      <Chip
                        icon={<AttachFileIcon fontSize="small" />}
                        label={`${archivoCount} archivo${
                          archivoCount !== 1 ? "s" : ""
                        }`}
                        className={classes.fileCount}
                        size="small"
                      />
                    )}
                  </div>

                  {/* Preview de descripción */}
                  <Typography
                    variant="body1"
                    className={classes.descripcionPreview}
                  >
                    {portafolio.descripcion}
                  </Typography>

                  {/* Metadata */}
                  <div className={classes.metadataContainer}>
                    {creador && (
                      <div className={classes.empleadoInfo}>
                        <Avatar className={classes.empleadoAvatar}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Creada por
                          </Typography>
                          <Typography variant="body2">
                            {capitalizarPalabras(String(creador))}
                          </Typography>
                        </Box>
                      </div>
                    )}

                    {mostrarUltimaEdicion && ultimaModificacion?.empleado && (
                      <div className={classes.empleadoInfo}>
                        <Avatar className={classes.empleadoAvatar}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Última modificación
                          </Typography>
                          <Typography variant="body2">
                            {ultimaModificacion.hora} -{" "}
                            {capitalizarPalabras(
                              String(ultimaModificacion.empleado),
                            )}
                          </Typography>
                        </Box>
                      </div>
                    )}
                  </div>
                </CardContent>

                <CardActions className={classes.cardActions}>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Ver detalles y archivos">
                      <IconButton
                        onClick={() => handleExpandClick(portafolio.id)}
                        className={`${classes.expandButton} ${
                          isExpanded ? classes.expandButtonRotated : ""
                        }`}
                        size="small"
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Tooltip>

                    {puedeModificar && (
                      <Tooltip title="Editar portafolio">
                        <IconButton
                          onClick={() => onEditarPortafolio(portafolio)}
                          className={classes.editButton}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip
                      title={
                        puedeEliminar
                          ? "Eliminar bitácora"
                          : "Solo ADMIN o SUPER_ADMIN pueden eliminar bitácoras"
                      }
                    >
                      <span>
                        <IconButton
                          onClick={() =>
                            puedeEliminar &&
                            handleEliminarPortafolio(portafolio)
                          }
                          className={
                            puedeEliminar
                              ? classes.deleteButton
                              : classes.deleteButtonDisabled
                          }
                          size="small"
                          disabled={
                            !puedeEliminar ||
                            (deletingPortafolio && deletingId === portafolio.id)
                          }
                        >
                          {deletingPortafolio &&
                          deletingId === portafolio.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  <Typography variant="caption" color="textSecondary">
                    {isExpanded ? "Ocultar detalles" : "Ver detalles"}
                  </Typography>
                </CardActions>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <PortafolioCardContent
                    portafolio={portafolio}
                    cliente={cliente}
                    puedeModificar={puedeModificar}
                    onDeleteFile={onDeleteFile}
                    deletingFile={deletingFile}
                  />
                </Collapse>
              </Card>
            </Fade>
          </Grid>
        );
      })}
    </Grid>
  );
}
